import { useState } from "react";
import { toast } from "sonner";
import { KeyRound, Mail, Copy } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";

const OPTS = [["meta", "Instagram & Facebook"], ["linkedin", "LinkedIn"]];

export default function SocialConnectOptions({ clientId, clientEmail, canConnect }) {
  const [email, setEmail] = useState(clientEmail || "");
  const [providers, setProviders] = useState(["meta", "linkedin"]);
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState("");
  const toggle = (p) => setProviders(ps => ps.includes(p) ? ps.filter(x => x !== p) : [...ps, p]);

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/clients/${clientId}/social/invite`, { email: email || clientEmail, providers });
      setLink(data.link);
      if (data.emailed) toast.success(`Connect link emailed to ${email || clientEmail}`);
      else toast.warning("The link was created but the email could not be sent. Copy the link below and share it.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const copy = async () => { try { await navigator.clipboard.writeText(link); toast.success("Link copied"); } catch { toast.error("Copy failed - select the link and copy it"); } };

  return (
    <div className="grid md:grid-cols-2 gap-4" data-testid="social-connect-options">
      <div className="panel p-5 space-y-2" data-testid="connect-option-creds">
        <div className="font-semibold flex items-center gap-2"><KeyRound className="w-4 h-4 text-orange-600" /> Option 1 · We have the client's login</div>
        <p className="text-sm text-neutral-600">Click <b>Connect Meta</b> or <b>Connect LinkedIn</b> below. In the window that opens, sign in with the client's Instagram/Facebook or LinkedIn login and approve access. Their password is never stored in this app.</p>
        <p className="text-[11px] text-neutral-500">Tip: log out of your own Facebook/LinkedIn first (or use a private window) so you sign in as the client.</p>
      </div>
      <div className="panel p-5 space-y-3" data-testid="connect-option-email">
        <div className="font-semibold flex items-center gap-2"><Mail className="w-4 h-4 text-orange-600" /> Option 2 · Email the client a connect link</div>
        <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="client@company.com" disabled={!canConnect} data-testid="invite-email" />
        <div className="flex gap-4">
          {OPTS.map(([k, l]) => (
            <label key={k} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`invite-provider-${k}`}>
              <Checkbox checked={providers.includes(k)} disabled={!canConnect} onCheckedChange={() => toggle(k)} /> {l}
            </label>
          ))}
        </div>
        <Button onClick={send} disabled={!canConnect || busy || !providers.length || !email.trim()} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="invite-send">{busy ? "Sending…" : "Send connect link"}</Button>
        {!canConnect && <p className="text-[11px] text-neutral-500">Only an Admin can send connect links.</p>}
        {link && (
          <div className="flex items-center gap-2 text-xs bg-[#F5F7FB] border border-[#E4E7EC] rounded-md px-2 py-1.5" data-testid="invite-link">
            <span className="truncate flex-1 font-mono">{link}</span>
            <button onClick={copy} className="p-1 text-orange-700" data-testid="invite-copy"><Copy className="w-4 h-4" /></button>
          </div>
        )}
        <p className="text-[11px] text-neutral-500">The client signs in on their own and approves. The link works for 7 days.</p>
      </div>
    </div>
  );
}
